import React, { useCallback, useEffect, useState } from 'react';
import { Upload, Button, Select, InputNumber, Table, Image, Tag, message, Divider, Popconfirm } from 'antd';
import { UploadOutlined, CheckCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import type { UploadFile } from 'antd/es/upload/interface';

interface AwardImageEntry {
  year: string;
  half: string;
  awardType: string;
  url: string;
}

const AWARD_TYPES = ['ACE', 'AVIA'];
const HALVES = [
  { label: 'H1 (Jan – Jun)', value: 'H1' },
  { label: 'H2 (Jul – Dec)', value: 'H2' },
];
const currentYear = new Date().getFullYear();
const currentHalf = new Date().getMonth() < 6 ? 'H1' : 'H2';

const AwardImageUpload: React.FC = () => {
  const { instance, accounts } = useMsal();
  const baseURL = process.env.REACT_APP_API_URL;

  const [year, setYear]           = useState<number>(currentYear);
  const [half, setHalf]           = useState<string>(currentHalf);
  const [awardType, setAwardType] = useState<string>('ACE');
  const [fileList, setFileList]   = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded]   = useState<AwardImageEntry[]>([]);

  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return res.idToken;
  }, [instance, accounts]);

  const loadList = useCallback(async () => {
    try {
      const token = await fetchToken();
      const res = await fetch(`${baseURL}/api/AwardImage/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setUploaded(await res.json());
    } catch { /* ignore */ }
  }, [fetchToken, baseURL]);

  useEffect(() => { loadList(); }, [loadList]);

  const handleDelete = async (record: AwardImageEntry) => {
    try {
      const token = await fetchToken();
      const params = new URLSearchParams({ year: record.year, half: record.half, awardType: record.awardType });
      const res = await fetch(`${baseURL}/api/AwardImage/delete?${params}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        message.success(`${record.awardType} image for ${record.year} ${record.half} deleted.`);
        loadList();
      } else {
        const err = await res.text();
        message.error(err || 'Delete failed.');
      }
    } catch {
      message.error('Delete failed.');
    }
  };

  const handleUpload = async () => {
    if (!fileList.length) { message.warning('Please select an image file.'); return; }
    const file = fileList[0] as any;
    setUploading(true);
    try {
      const token = await fetchToken();
      const form = new FormData();
      form.append('year', String(year));
      form.append('half', half);
      form.append('awardType', awardType);
      form.append('image', file.originFileObj ?? file);

      const res = await fetch(`${baseURL}/api/AwardImage/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (res.ok) {
        message.success(`${awardType} image for ${year} ${half} uploaded successfully.`);
        setFileList([]);
        loadList();
      } else {
        const err = await res.text();
        message.error(err || 'Upload failed.');
      }
    } catch {
      message.error('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    { title: 'Year',       dataIndex: 'year',      key: 'year',      sorter: (a: AwardImageEntry, b: AwardImageEntry) => Number(b.year) - Number(a.year) },
    { title: 'Half',       dataIndex: 'half',      key: 'half',      render: (h: string) => <Tag color={h === 'H1' ? 'blue' : 'orange'}>{h}</Tag> },
    { title: 'Award Type', dataIndex: 'awardType', key: 'awardType', render: (t: string) => <Tag color="purple">{t}</Tag> },
    {
      title: 'Preview',
      dataIndex: 'url',
      key: 'preview',
      render: (url: string) => (
        <Image
          src={`${baseURL}${url}`}
          alt="award"
          width={80}
          height={50}
          style={{ objectFit: 'contain', borderRadius: 4, border: '1px solid #f0f0f0' }}
        />
      ),
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      render: (url: string) => <span style={{ fontSize: 12, color: '#888' }}>{url}</span>,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: AwardImageEntry) => (
        <Popconfirm
          title="Delete this image permanently?"
          onConfirm={() => handleDelete(record)}
          okText="Yes, Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button danger icon={<DeleteOutlined />} size="small">Delete</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 800 }}>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Upload the banner image shown on feeds for each award type per year.
        Each year's feeds will automatically use that year's image.
      </p>

      {/* Upload form */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Year</div>
          <InputNumber
            min={2000} max={2100}
            value={year}
            onChange={v => setYear(v ?? currentYear)}
            style={{ width: 100 }}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Half</div>
          <Select
            value={half}
            onChange={setHalf}
            style={{ width: 150 }}
            options={HALVES}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Award Type</div>
          <Select
            value={awardType}
            onChange={setAwardType}
            style={{ width: 120 }}
            options={AWARD_TYPES.map(t => ({ label: t, value: t }))}
          />
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Image (PNG / JPG)</div>
          <Upload
            accept=".png,.jpg,.jpeg"
            maxCount={1}
            fileList={fileList}
            beforeUpload={() => false}
            onChange={({ fileList: fl }) => setFileList(fl)}
          >
            <Button icon={<UploadOutlined />}>Select Image</Button>
          </Upload>
        </div>
        <Button
          type="primary"
          icon={<CheckCircleOutlined />}
          loading={uploading}
          onClick={handleUpload}
          disabled={!fileList.length}
          style={{ background: '#481558', borderColor: '#481558' }}
        >
          Upload
        </Button>
      </div>

      <Divider />

      {/* Existing images */}
      <h4 style={{ marginBottom: 12 }}>Uploaded Images</h4>
      <Table
        dataSource={uploaded}
        columns={columns}
        rowKey={r => `${r.year}_${r.half}_${r.awardType}`}
        size="small"
        pagination={false}
        locale={{ emptyText: 'No images uploaded yet.' }}
      />
    </div>
  );
};

export default AwardImageUpload;
